// v5
// Path: frontend/src/components/QuickBooksSync/DryRunDiffModal.jsx
// Inputs: token, config, onClose, onSyncCompleted
// Outputs: React JSX modal allowing real-time diff preview, connection info display, PBS and QuickBooks sample data inspection, detailed error diagnostics, and confirmed live sync
// Summary: Dry-run and live execution inspector comparing PBS ERP tables to QuickBooks Online with connection details breakdown, diff analysis, source & destination sample data viewers, granular error diagnostics, and pre-flight sync execution confirmation.

import React, { useState, useEffect } from 'react';


// Auto-injected unbox helper for standardized API responses
const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

export const DryRunDiffModal = ({ token, config, onClose, onSyncCompleted }) => {
    const [recordLimit, setRecordLimit] = useState(50);
    const [activeTab, setActiveTab] = useState('diff'); // 'diff', 'source', or 'qb'
    const [loadingPbs, setLoadingPbs] = useState(false);
    const [loadingQb, setLoadingQb] = useState(false);
    const [loadingDiff, setLoadingDiff] = useState(false);
    const [loadingSync, setLoadingSync] = useState(false);
    const [pbsPreviewData, setPbsPreviewData] = useState(null);
    const [qbPreviewData, setQbPreviewData] = useState(null);
    const [qbSearchTerm, setQbSearchTerm] = useState('');
    const [diffPreviewData, setDiffPreviewData] = useState(null);
    const [diffError, setDiffError] = useState(null);
    const [syncResult, setSyncResult] = useState(null);
    const [showConfirmSync, setShowConfirmSync] = useState(false);

    // Parse SourceConnPrefix into readable components
    const parsedConn = (() => {
        const p = config.SourceConnPrefix;
        if (!p || !p.startsWith('[')) return null;
        const cb = p.indexOf(']');
        if (cb === -1) return null;
        const parts = p.substring(1, cb).split('|');
        if (parts.length === 5) {
            return {
                serverName: parts[0],
                ip: parts[1],
                instance: parts[2],
                port: parts[3],
                user: parts[4]
            };
        }
        return null;
    })();

    // Auto-run diff when modal opens
    useEffect(() => {
        handleRunDryRun();
    }, [config]);

    const handleFetchPbsSample = async () => {
        setLoadingPbs(true);
        setPbsPreviewData(null);
        setActiveTab('source');
        try {
            const params = new URLSearchParams({
                sourceDatabaseName: config.SourceDatabaseName,
                entityType: config.EntityType,
                limit: String(recordLimit)
            });
            if (config.SourceConnPrefix) {
                params.append('sourceConnPrefix', config.SourceConnPrefix);
            }
            if (config.TableName) {
                params.append('tableName', config.TableName);
            }

            const res = await fetch(`/api/sync/pbs-preview?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) {
                const err = await unbox(res);
                throw new Error(err.error || 'Failed to fetch PBS source sample');
            }

            const data = await unbox(res);
            setPbsPreviewData(data);
        } catch (err) {
            useSyncStore.getState().showAlert(`PBS Fetch Error: ${err.message}`);
        } finally {
            setLoadingPbs(false);
        }
    };

    const handleFetchQbSample = async () => {
        setLoadingQb(true);
        setQbPreviewData(null);
        setActiveTab('qb');
        try {
            const entity = config.TargetQBEntity || config.EntityType || 'Customer';
            const params = new URLSearchParams({
                accountId: String(config.AccountID),
                entity: entity,
                limit: String(recordLimit)
            });

            const res = await fetch(`/api/sync/qb-preview?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) {
                const err = await unbox(res);
                throw new Error(err.error || 'Failed to fetch QuickBooks sample records');
            }

            const data = await unbox(res);
            setQbPreviewData(data);
        } catch (err) {
            useSyncStore.getState().showAlert(`QuickBooks Fetch Error: ${err.message}`);
        } finally {
            setLoadingQb(false);
        }
    };

    const handleRunDryRun = async () => {
        setLoadingDiff(true);
        setDiffPreviewData(null);
        setDiffError(null);
        setSyncResult(null);
        setActiveTab('diff');
        try {
            const payload = {
                configId: config.ConfigID,
                accountId: config.AccountID,
                sourceConnPrefix: config.SourceConnPrefix,
                sourceDatabaseName: config.SourceDatabaseName,
                tableName: config.TableName,
                targetQBEntity: config.TargetQBEntity,
                entityType: config.EntityType,
                limit: recordLimit
            };

            const res = await fetch('/api/sync/sync/preview', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await unbox(res);
                throw new Error(err.error || 'Dry run preview failed');
            }

            const data = await unbox(res);
            setDiffPreviewData(data);
        } catch (err) {
            console.error('Diff error:', err);
            setDiffError(err.message);
        } finally {
            setLoadingDiff(false);
        }
    };

    const handleOpenSyncConfirmation = () => {
        setShowConfirmSync(true);
    };

    const handleExecuteLiveSyncConfirmed = async () => {
        setShowConfirmSync(false);
        setLoadingSync(true);
        setSyncResult(null);
        try {
            const payload = {
                configId: config.ConfigID,
                accountId: config.AccountID,
                sourceConnPrefix: config.SourceConnPrefix,
                sourceDatabaseName: config.SourceDatabaseName,
                tableName: config.TableName,
                targetQBEntity: config.TargetQBEntity,
                entityType: config.EntityType,
                limit: recordLimit
            };

            const res = await fetch('/api/sync/sync/execute', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await unbox(res);
                throw new Error(err.error || 'Live sync execution failed');
            }

            const data = await unbox(res);
            setSyncResult(data);
            if (onSyncCompleted) onSyncCompleted();
        } catch (err) {
            useSyncStore.getState().showAlert(`Sync Execution Error: ${err.message}`);
        } finally {
            setLoadingSync(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="p-5 border-b border-slate-700 flex justify-between items-center bg-slate-800/90">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-600/20 border border-blue-500/40 flex items-center justify-center text-blue-400">
                            <span className="material-symbols-outlined">preview</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-base font-bold text-white">{config.ConfigName}</h3>
                                <span className="px-2 py-0.5 rounded text-[11px] font-mono bg-blue-950 text-blue-300 border border-blue-800">
                                    {config.EntityType}
                                </span>
                            </div>
                            <p className="text-xs text-slate-400">
                                Source: <code className="text-slate-300">{config.SourceDatabaseName}</code> &rarr; Target QB: <span className="text-emerald-300">{config.AccountName || 'QuickBooks'}</span>
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Connection & Target Settings Card */}
                <div className="bg-slate-900/90 border-b border-slate-700/80 px-5 py-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div className="space-y-0.5">
                            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Source SQL Instance</span>
                            <div className="text-white font-medium flex items-center gap-1">
                                <span className="material-symbols-outlined text-[14px] text-emerald-400">dns</span>
                                <span className="truncate">{parsedConn ? `${parsedConn.serverName} (${parsedConn.ip}:${parsedConn.port})` : (config.SourceConnPrefix || 'Local SQL')}</span>
                            </div>
                            <div className="text-[11px] text-slate-400 font-mono">
                                Instance: {parsedConn?.instance || 'ssinstance'} &bull; User: {parsedConn?.user || 'sa'}
                            </div>
                        </div>

                        <div className="space-y-0.5">
                            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Database & Source Table</span>
                            <div className="text-white font-medium flex items-center gap-1 font-mono">
                                <span className="material-symbols-outlined text-[14px] text-sky-400">table_chart</span>
                                <span className="text-sky-300 font-bold">{config.SourceDatabaseName}</span>
                            </div>
                            <div className="text-[11px] text-slate-300 font-mono">
                                Table: <strong className="text-emerald-300">{config.TableName || config.EntityType}</strong>
                            </div>
                        </div>

                        <div className="space-y-0.5">
                            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">QuickBooks Destination</span>
                            <div className="text-white font-medium flex items-center gap-1">
                                <span className="material-symbols-outlined text-[14px] text-purple-400">account_balance</span>
                                <span className="truncate">{config.AccountName || 'Connected Account'}</span>
                            </div>
                            <div className="text-[11px] text-slate-400">
                                Target Entity: <strong className="text-purple-300">{config.TargetQBEntity || config.EntityType}</strong> &bull; <span className="uppercase text-[10px] px-1 py-0.2 bg-slate-800 rounded">{config.Environment || 'sandbox'}</span>
                            </div>
                        </div>

                        <div className="space-y-0.5">
                            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Pipeline Strategy</span>
                            <div className="text-slate-300 flex items-center gap-1 font-medium">
                                <span className="material-symbols-outlined text-[14px] text-amber-400">sync_alt</span>
                                <span>{config.SyncDirection || 'PBS_TO_QB'}</span>
                            </div>
                            <div className="text-[11px] text-slate-400">
                                Policy: <strong className="text-slate-300">{config.ConflictPolicy || 'SOURCE_WINS'}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Control Bar */}
                <div className="p-4 bg-slate-900/80 border-b border-slate-700/80 flex flex-wrap items-center justify-between gap-3 text-xs">
                    <div className="flex items-center gap-2">
                        <span className="text-slate-400">Batch Limit:</span>
                        <select
                            value={recordLimit}
                            onChange={e => setRecordLimit(parseInt(e.target.value, 10))}
                            className="bg-slate-900 border border-slate-700 rounded px-2.5 py-1 text-white focus:outline-none focus:border-blue-500 font-medium"
                        >
                            <option value={10}>10 records</option>
                            <option value={50}>50 records</option>
                            <option value={100}>100 records</option>
                            <option value={500}>500 records</option>
                        </select>

                        <div className="flex bg-slate-800 border border-slate-700 rounded p-0.5 ml-2">
                            <button
                                onClick={() => setActiveTab('diff')}
                                className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${activeTab === 'diff' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                            >
                                Dry-Run Diff
                            </button>
                            <button
                                onClick={handleFetchPbsSample}
                                className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${activeTab === 'source' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                            >
                                Raw PBS Table
                            </button>
                            <button
                                onClick={handleFetchQbSample}
                                className={`px-3 py-1 rounded text-xs font-semibold transition-colors flex items-center gap-1 ${activeTab === 'qb' ? 'bg-purple-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                            >
                                <span className="material-symbols-outlined text-[13px]">database</span>
                                Live QB Sample
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleRunDryRun}
                            disabled={loadingDiff}
                            className="px-3.5 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded text-xs font-semibold flex items-center gap-1 transition-colors"
                        >
                            <span className="material-symbols-outlined text-[15px]">refresh</span>
                            {loadingDiff ? 'Diffing...' : 'Re-Run Diff'}
                        </button>
                        <button
                            onClick={handleOpenSyncConfirmation}
                            disabled={loadingSync}
                            className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded text-xs font-bold flex items-center gap-1.5 transition-colors shadow-sm"
                        >
                            <span className="material-symbols-outlined text-[15px]">bolt</span>
                            {loadingSync ? 'Syncing...' : 'Execute Live Sync'}
                        </button>
                    </div>
                </div>

                {/* Content View */}
                <div className="p-5 overflow-y-auto flex-1 space-y-4">
                    {/* Live Sync Result Banner */}
                    {syncResult && (
                        <div className={`p-4 rounded-lg border text-xs ${
                            syncResult.status === 'SUCCESS' 
                                ? 'bg-emerald-950/60 border-emerald-800 text-emerald-200' 
                                : syncResult.status === 'PARTIAL'
                                    ? 'bg-amber-950/60 border-amber-800 text-amber-200'
                                    : 'bg-red-950/60 border-red-800 text-red-200'
                        }`}>
                            <div className="flex justify-between items-start">
                                <div>
                                    <h4 className="font-bold text-sm flex items-center gap-1.5">
                                        <span className="material-symbols-outlined text-[18px]">
                                            {syncResult.status === 'SUCCESS' ? 'check_circle' : (syncResult.status === 'PARTIAL' ? 'warning' : 'error')}
                                        </span>
                                        Execution Status: {syncResult.status} ({syncResult.durationSeconds !== undefined ? `${syncResult.durationSeconds}s` : `${syncResult.durationMs || 0}ms`})
                                    </h4>
                                    <p className="mt-1">{syncResult.summary}</p>
                                </div>
                                <div className="flex gap-2 text-[11px] font-mono">
                                    <span className="px-2 py-0.5 rounded bg-emerald-900/60 text-emerald-300 border border-emerald-700">
                                        +{syncResult.recordsInserted ?? syncResult.inserted ?? 0} Created
                                    </span>
                                    <span className="px-2 py-0.5 rounded bg-blue-900/60 text-blue-300 border border-blue-700">
                                        {syncResult.recordsUpdated ?? syncResult.updated ?? 0} Updated
                                    </span>
                                    {((syncResult.recordsFailed || 0) > 0 || (syncResult.failed || 0) > 0) && (
                                        <span className="px-2 py-0.5 rounded bg-red-900/60 text-red-300 border border-red-700">
                                            {syncResult.recordsFailed ?? syncResult.failed ?? 0} Failed
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Granular Error Diagnostics List */}
                            {syncResult.errors && syncResult.errors.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-red-900/50">
                                    <div className="font-bold text-xs text-red-300 mb-1.5 flex items-center gap-1">
                                        <span className="material-symbols-outlined text-[15px]">report_problem</span>
                                        Error Diagnostics ({syncResult.errors.length} record{syncResult.errors.length === 1 ? '' : 's'} failed):
                                    </div>
                                    <div className="max-h-56 overflow-y-auto space-y-1.5 font-mono text-[11px] pr-1">
                                        {syncResult.errors.map((err, idx) => (
                                            <div key={idx} className="p-2.5 rounded bg-black/40 border border-red-800/80 text-red-200">
                                                <div className="font-semibold text-white flex items-center justify-between">
                                                    <span>Record [{err.key}]{err.name ? ` - ${err.name}` : ''}</span>
                                                    <span className="text-[10px] text-red-400">Failed</span>
                                                </div>
                                                <div className="text-red-300 mt-1 whitespace-pre-wrap">{err.error}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Error Banner */}
                    {diffError && (
                        <div className="p-4 rounded-lg border bg-red-950/70 border-red-800 text-red-200 text-xs">
                            <div className="flex items-start gap-2">
                                <span className="material-symbols-outlined text-[20px] text-red-400">error</span>
                                <div>
                                    <h4 className="font-bold text-sm text-red-300">Diff Preview Failed</h4>
                                    <p className="mt-1 font-mono text-[11px] text-red-200/90">{diffError}</p>
                                    <p className="mt-2 text-slate-300">
                                        Tip: Ensure the Database Profile ({config.SourceConnPrefix || 'Default'}) is online and reachable, and the database <code>{config.SourceDatabaseName}</code> contains the ERP tables.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'diff' ? (
                        loadingDiff ? (
                            <div className="p-16 text-center text-slate-400">
                                <span className="material-symbols-outlined animate-spin text-3xl mb-2 text-blue-400">sync</span>
                                <p>Analyzing PBS ERP records against QuickBooks Online records...</p>
                            </div>
                        ) : diffPreviewData ? (
                            <div className="space-y-4">
                                {/* Stats Cards */}
                                <div className="grid grid-cols-4 gap-3">
                                    <div className="bg-slate-900/70 border border-slate-700/70 rounded-lg p-3 text-center">
                                        <div className="text-xl font-bold text-white">{diffPreviewData.totalPbsRecords}</div>
                                        <div className="text-[11px] text-slate-400">PBS Records Read</div>
                                    </div>
                                    <div className="bg-slate-900/70 border border-slate-700/70 rounded-lg p-3 text-center">
                                        <div className="text-xl font-bold text-emerald-400">+{diffPreviewData.toCreate.length}</div>
                                        <div className="text-[11px] text-slate-400">New in QuickBooks</div>
                                    </div>
                                    <div className="bg-slate-900/70 border border-slate-700/70 rounded-lg p-3 text-center">
                                        <div className="text-xl font-bold text-blue-400">{diffPreviewData.toUpdate.length}</div>
                                        <div className="text-[11px] text-slate-400">Existing (Updates)</div>
                                    </div>
                                    <div className="bg-slate-900/70 border border-slate-700/70 rounded-lg p-3 text-center">
                                        <div className="text-xl font-bold text-slate-300">{diffPreviewData.totalQbRecords}</div>
                                        <div className="text-[11px] text-slate-400">Total in QuickBooks</div>
                                    </div>
                                </div>

                                {/* Sample Mappings List */}
                                <div className="space-y-2">
                                    <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
                                        Sample Mappings & Transformation Diff ({diffPreviewData.sampleMappings.length})
                                    </h4>
                                    <div className="space-y-2">
                                        {diffPreviewData.sampleMappings.map((sample, idx) => (
                                            <div key={idx} className="bg-slate-900/90 border border-slate-700/80 rounded-lg p-3 text-xs space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${sample.action === 'CREATE' ? 'bg-emerald-950 text-emerald-300 border border-emerald-800' : 'bg-blue-950 text-blue-300 border border-blue-800'}`}>
                                                            {sample.action}
                                                        </span>
                                                        <span className="font-mono font-semibold text-white">{sample.sourceKey}</span>
                                                        <span className="text-slate-400">&bull;</span>
                                                        <span className="text-slate-200 font-medium">{sample.sourceName}</span>
                                                    </div>
                                                </div>
                                                <pre className="p-2.5 bg-slate-950 rounded text-[11px] font-mono text-blue-300 overflow-x-auto border border-slate-800">
                                                    {JSON.stringify(sample.mappedPayload, null, 2)}
                                                </pre>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="p-12 text-center text-slate-400">
                                Click "Re-Run Diff" to analyze transformations.
                            </div>
                        )
                    ) : (
                        loadingPbs ? (
                            <div className="p-16 text-center text-slate-400">
                                <span className="material-symbols-outlined animate-spin text-3xl mb-2 text-blue-400">sync</span>
                                <p>Extracting raw table records from PBS ERP database...</p>
                            </div>
                        ) : pbsPreviewData ? (
                            <div className="space-y-3">
                                <div className="text-xs text-slate-400">
                                    Extracted <strong>{pbsPreviewData.count}</strong> rows from table <code className="text-blue-300">{pbsPreviewData.table}</code>
                                </div>
                                <div className="border border-slate-700 rounded-lg overflow-x-auto max-h-96">
                                    <table className="w-full text-left text-xs text-slate-300">
                                        <thead className="bg-slate-900 text-slate-400 font-semibold sticky top-0 border-b border-slate-700">
                                            <tr>
                                                {pbsPreviewData.records.length > 0 && Object.keys(pbsPreviewData.records[0]).map(col => (
                                                    <th key={col} className="p-2.5 font-mono whitespace-nowrap">{col}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-700/60 font-mono text-[11px]">
                                            {pbsPreviewData.records.map((row, rIdx) => (
                                                <tr key={rIdx} className="hover:bg-slate-750">
                                                    {Object.values(row).map((val, cIdx) => (
                                                        <td key={cIdx} className="p-2.5 whitespace-nowrap text-slate-200">
                                                            {val !== null && val !== undefined ? String(val) : <span className="text-slate-600">NULL</span>}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="p-12 text-center text-slate-400">
                                Click "Raw PBS Table" to query sample records from the SQL database.
                            </div>
                        )
                    )}

                    {/* Live QuickBooks Samples Tab */}
                    {activeTab === 'qb' && (
                        loadingQb ? (
                            <div className="p-12 text-center text-slate-400">
                                <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                                <p>Querying live sample records from QuickBooks Online ({config.TargetQBEntity || config.EntityType})...</p>
                            </div>
                        ) : qbPreviewData ? (
                            <div className="space-y-4">
                                <div className="flex flex-wrap justify-between items-center gap-2 text-xs bg-slate-900/80 p-3 rounded-lg border border-slate-800">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-full bg-purple-400"></span>
                                        <span className="text-slate-300">
                                            Found <strong className="text-white">{qbPreviewData.count}</strong> live records for entity <code className="text-purple-300 font-bold">{qbPreviewData.entity}</code>
                                        </span>
                                        {qbPreviewData.companyName && (
                                            <span className="text-slate-500 font-mono text-[11px]">
                                                &bull; Company: {qbPreviewData.companyName}
                                            </span>
                                        )}
                                    </div>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Search QB fields..."
                                            value={qbSearchTerm}
                                            onChange={e => setQbSearchTerm(e.target.value)}
                                            className="bg-slate-950 border border-slate-750 rounded px-2.5 py-1 text-slate-200 placeholder-slate-500 text-xs focus:outline-none focus:border-purple-400 w-48"
                                        />
                                    </div>
                                </div>

                                {qbPreviewData.records.length === 0 ? (
                                    <div className="p-10 text-center text-slate-500 text-xs italic bg-slate-900/40 rounded-lg border border-slate-800">
                                        No {qbPreviewData.entity} records currently exist in this QuickBooks company.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {qbPreviewData.records
                                            .filter(rec => !qbSearchTerm || JSON.stringify(rec).toLowerCase().includes(qbSearchTerm.toLowerCase()))
                                            .map((rec, idx) => {
                                                const title = rec.DisplayName || rec.Name || rec.DocNumber || `ID: ${rec.Id}`;
                                                const subtitle = rec.CompanyName || rec.AcctNum || (rec.TotalAmt !== undefined ? `$${rec.TotalAmt}` : rec.Type || '');
                                                return (
                                                    <div key={rec.Id || idx} className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden shadow-sm">
                                                        <div className="p-3 bg-slate-850 border-b border-slate-800 flex justify-between items-center text-xs">
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-bold text-white text-sm">{title}</span>
                                                                {subtitle && <span className="text-slate-400 font-medium text-xs">&bull; {subtitle}</span>}
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-mono text-purple-300 bg-purple-950/70 border border-purple-800 px-2 py-0.5 rounded text-[11px]">
                                                                    ID: {rec.Id}
                                                                </span>
                                                                <span className="font-mono text-slate-400 bg-slate-800 px-2 py-0.5 rounded text-[10px]">
                                                                    SyncToken: {rec.SyncToken ?? 'N/A'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="p-3 bg-slate-950/90 text-xs">
                                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                                                                <div className="p-2 rounded bg-slate-900 border border-slate-800">
                                                                    <span className="text-slate-500 text-[10px] block font-bold uppercase">AcctNum / Code</span>
                                                                    <span className="text-slate-200 font-mono text-xs">{rec.AcctNum || '—'}</span>
                                                                </div>
                                                                <div className="p-2 rounded bg-slate-900 border border-slate-800">
                                                                    <span className="text-slate-500 text-[10px] block font-bold uppercase">Phone</span>
                                                                    <span className="text-slate-200 text-xs">{rec.PrimaryPhone?.FreeFormNumber || '—'}</span>
                                                                </div>
                                                                <div className="p-2 rounded bg-slate-900 border border-slate-800">
                                                                    <span className="text-slate-500 text-[10px] block font-bold uppercase">Email</span>
                                                                    <span className="text-slate-200 text-xs truncate block">{rec.PrimaryEmailAddr?.Address || '—'}</span>
                                                                </div>
                                                                <div className="p-2 rounded bg-slate-900 border border-slate-800">
                                                                    <span className="text-slate-500 text-[10px] block font-bold uppercase">Active Status</span>
                                                                    <span className={`text-xs font-semibold ${rec.Active ? 'text-emerald-400' : 'text-slate-500'}`}>
                                                                        {rec.Active !== undefined ? (rec.Active ? 'Active' : 'Inactive') : '—'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <details className="mt-2 text-slate-400">
                                                                <summary className="cursor-pointer text-[11px] font-semibold text-purple-400 hover:text-purple-300">
                                                                    View Full Raw QBO Payload ({Object.keys(rec).length} fields)
                                                                </summary>
                                                                <pre className="mt-2 p-2.5 bg-slate-900 border border-slate-800 rounded font-mono text-[11px] text-slate-300 overflow-x-auto max-h-60 leading-relaxed">
                                                                    {JSON.stringify(rec, null, 2)}
                                                                </pre>
                                                            </details>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="p-12 text-center text-slate-400">
                                Click "Live QB Sample" to query sample records from the connected QuickBooks company.
                            </div>
                        )
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-700 flex justify-end bg-slate-800/90">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm transition-colors"
                    >
                        Close Inspector
                    </button>
                </div>
            </div>

            {/* Pre-Sync Execution Confirmation Modal */}
            {showConfirmSync && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-lg w-full shadow-2xl overflow-hidden p-6 space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 shrink-0">
                                <span className="material-symbols-outlined text-2xl">warning</span>
                            </div>
                            <div>
                                <h4 className="text-base font-bold text-white">Confirm Live QuickBooks Synchronization</h4>
                                <p className="text-xs text-slate-400">Please verify connection parameters before writing to QuickBooks Online.</p>
                            </div>
                        </div>

                        <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 space-y-2 text-xs">
                            <div className="flex justify-between py-1 border-b border-slate-800">
                                <span className="text-slate-400">Source Server Instance:</span>
                                <span className="text-slate-200 font-mono font-semibold">{parsedConn ? `${parsedConn.serverName} (${parsedConn.ip}:${parsedConn.port})` : config.SourceConnPrefix}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-slate-800">
                                <span className="text-slate-400">Database & Table:</span>
                                <span className="text-sky-300 font-mono font-semibold">[{config.SourceDatabaseName}].[dbo].[{config.TableName || config.EntityType}]</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-slate-800">
                                <span className="text-slate-400">Target QuickBooks Account:</span>
                                <span className="text-purple-300 font-semibold">{config.AccountName || 'QuickBooks Account'}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-slate-800">
                                <span className="text-slate-400">Destination Entity:</span>
                                <span className="text-purple-200 font-bold">{config.TargetQBEntity || config.EntityType}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-slate-800">
                                <span className="text-slate-400">Batch Record Limit:</span>
                                <span className="text-white font-bold">{recordLimit} records</span>
                            </div>
                            <div className="flex justify-between py-1">
                                <span className="text-slate-400">Conflict Policy / Direction:</span>
                                <span className="text-amber-300">{config.ConflictPolicy || 'SOURCE_WINS'} ({config.SyncDirection || 'PBS_TO_QB'})</span>
                            </div>
                        </div>

                        {diffPreviewData && (
                            <div className="p-3 bg-emerald-950/40 border border-emerald-800/60 rounded-lg text-xs space-y-1">
                                <div className="text-emerald-300 font-semibold flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-[16px]">insights</span>
                                    <span>Diff Analysis Ready:</span>
                                </div>
                                <div className="text-slate-300 pl-5">
                                    &bull; <strong>{diffPreviewData.toCreate?.length || 0}</strong> record(s) will be <strong>CREATED</strong> in QuickBooks<br/>
                                    &bull; <strong>{diffPreviewData.toUpdate?.length || 0}</strong> record(s) will be <strong>UPDATED</strong> in QuickBooks<br/>
                                    &bull; <strong>{diffPreviewData.totalPbsRecords || 0}</strong> total record(s) scanned from PBS
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => setShowConfirmSync(false)}
                                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleExecuteLiveSyncConfirmed}
                                disabled={loadingSync}
                                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors shadow-lg shadow-emerald-900/30"
                            >
                                <span className="material-symbols-outlined text-[16px]">bolt</span>
                                {loadingSync ? 'Executing Live Sync...' : 'Confirm & Execute Live Sync'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
